/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import type { QueryHookOptions } from '@apollo/client';
import { useQuery } from '@apollo/client';

import {
  convertRawDataToPlan,
  getIsPlanInaccessible,
} from '@fai/friendli-suite/features/products/dedicated-endpoints/plan';
import {
  TeamDedicatedEndpointsPlanDocument,
  type TeamDedicatedEndpointsPlanQuery,
  type TeamDedicatedEndpointsPlanQueryVariables,
} from '@fai/friendli-suite/generated/client/graphql';

export const useDedicatedEndpointsTeamPlan = (
  teamId: string,
  queryOptions?: QueryHookOptions<
    TeamDedicatedEndpointsPlanQuery,
    TeamDedicatedEndpointsPlanQueryVariables
  >
) => {
  const {
    data: rawData,
    loading,
    error,
  } = useQuery(TeamDedicatedEndpointsPlanDocument, {
    variables: { teamId },
    fetchPolicy: 'network-only',
    ...queryOptions,
  });

  const userTeamData = rawData?.clientSession?.user.team;
  const productDedicatedData = userTeamData?.team.dedicatedSubplan;

  const teamRole = userTeamData?.role;
  const isProductAccessible = userTeamData?.productAccess?.dedicatedEndpoints;
  const productPlan = productDedicatedData?.tier;

  const plan = convertRawDataToPlan({
    teamRole,
    isProductAccessible,
    productPlan,
  });

  const isPlanInaccessible = getIsPlanInaccessible(plan);

  return { plan, isPlanInaccessible, loading, error };
};
